import React, { useEffect } from 'react';
import { Navigate, Outlet, useLocation, useSearchParams } from 'react-router-dom';
import useAuth from '@/routes/hooks/useAuth';
import { Loading } from '@/components/Loading';
import { AppRoutes, BRANDED_DOMAINS, UNBRANDED_DOMAINS } from 'src/enums/ERoutes';
import { useQuery } from '@tanstack/react-query';
import {
	getResellerByCode,
	getResellerByDomain,
	getResellerLogo,
} from '@/api/unbrandedReceptionist';
import { useFavicon } from './hooks/useFavicon';

const UserPublicRoute: React.FC = () => {
	const { isAuthenticated, isLoading } = useAuth();
	const location = useLocation();
	const domain = window.location.hostname.replace(/^www\./, '');
	const [searchParams] = useSearchParams();

	const isUnbrandedPath =
		location.pathname === AppRoutes.UNBRANDED_RECEPTIONIST ||
		location.pathname === AppRoutes.UNBRANDED_INBOUND_AGENT_PORTAL ||
		UNBRANDED_DOMAINS.some((unbrandedDomain) => domain.includes(unbrandedDomain));

	const resellerCode = searchParams.get('reseller_code') || searchParams.get('code');
	const isCustomDomain = !BRANDED_DOMAINS.includes(domain) && !UNBRANDED_DOMAINS.includes(domain);
	const { data: resellerData, refetch: refetchReseller } = useQuery({
		queryKey: isCustomDomain ? ['reseller-domain', domain] : ['reseller-code', resellerCode],
		queryFn: () =>
			isCustomDomain
				? getResellerByDomain({ domain })
				: getResellerByCode({ resellerCode: resellerCode || '' }),
		enabled: isCustomDomain || !!resellerCode,
	});

	const { data: resellerLogo } = useQuery({
		queryKey: ['reseller-logo'],
		queryFn: () => getResellerLogo({ key: resellerData.reseller.logo }),
		enabled: !!(isUnbrandedPath || resellerData?.reseller?.logo),
		refetchOnWindowFocus: false,
		refetchOnMount: false,
	});

	useFavicon({
		isUnbrandedPath,
		resellerCode,
		resellerLogo,
		refetchReseller,
	});

	useEffect(() => {
		if (
			typeof window !== 'undefined' &&
			(location.pathname === AppRoutes.UNBRANDED_RECEPTIONIST ||
				location.pathname === AppRoutes.UNBRANDED_INBOUND_AGENT_PORTAL)
		) {
			try {
				document.title = 'Portal';
				const metaDescription = document.querySelector('meta[name="description"]');
				if (metaDescription) {
					metaDescription.setAttribute('content', 'Dashboard/Portal');
				}
				const pageTitle = document.getElementById('page-title');
				if (pageTitle) {
					pageTitle.textContent = 'Portal';
				}
			} catch (err) {
				console.error('Error updating page metadata:', err);
			}
		}
	}, [isUnbrandedPath]);

	if (isLoading) {
		return <Loading />;
	}

	if (!isAuthenticated && isUnbrandedPath) {
		return <Outlet context={{ debrand: isUnbrandedPath }} />;
	} else if (!isAuthenticated && !isUnbrandedPath) {
		return <Outlet />;
	}

	if (isAuthenticated && isUnbrandedPath) {
		return (
			<Navigate
				to="/dashboard"
				state={{ from: location.pathname, debrand: isUnbrandedPath, resellerCode }}
				replace
			/>
		);
	} else if (isAuthenticated && !isUnbrandedPath) {
		return <Navigate to="/" />;
	}

	return <Outlet />;
};

export default UserPublicRoute;
